import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES, FARES, EASE } from "../constants/site";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import Magnetic from "../components/Magnetic";
import FleetCard from "../components/FleetCard";
import { useSeo } from "../hooks/useSeo";

const MODELS = (t) => [
  {
    id: "model-3",
    name: t.fleetPage.models.m3.name,
    category: t.fleetPage.models.m3.category,
    range: "629 km",
    accel: "4.4s",
    seats: "4",
    luggage: "2+2",
    labels: t.fleetPage.labels
  },
  {
    id: "model-y",
    name: t.fleetPage.models.my.name,
    category: t.fleetPage.models.my.category,
    range: "533 km",
    accel: "5.0s",
    seats: "4–5",
    luggage: "4",
    labels: t.fleetPage.labels
  }
];

function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.24]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 55, damping: 16 });
  const sy = useSpring(py, { stiffness: 55, damping: 16 });
  const tiltX = useTransform(sy, [-0.5, 0.5], [2.6, -2.6]);
  const tiltY = useTransform(sx, [-0.5, 0.5], [-3.2, 3.2]);
  const imgX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const spotLeft = useTransform(sx, [-0.5, 0.5], ["18%", "82%"]);
  const spotTop = useTransform(sy, [-0.5, 0.5], ["12%", "88%"]);

  const onPointerMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onPointerMove={onPointerMove} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col overflow-hidden">
      <motion.div style={{ y: imgY, x: imgX, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Tesla Model 3 — Antwerp"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070709] via-[#070709]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-transparent to-[#070709]/70" />
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ left: spotLeft, top: spotTop }}
        className="pointer-events-none absolute z-[5] h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-3xl"
      />

      <motion.div style={{ opacity: contentFade, rotateX: tiltX, rotateY: tiltY, transformPerspective: 1100 }} className="relative z-10 flex flex-1 items-end lg:items-center">
        <div className="mx-auto w-full max-w-7xl px-5 lg:px-10 pt-32 pb-14 lg:py-40">
          <motion.p
            data-testid="hero-badge"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
            className="eyebrow inline-flex items-center gap-2 rounded-full border border-gold/25 bg-black/40 backdrop-blur px-3 py-1.5 sm:px-4 sm:py-2 text-[10px] sm:text-[11px] max-w-full"
          >
            {t.hero.badge}
          </motion.p>

          <h1 className="mt-7 font-display font-extrabold uppercase tracking-tighter sm:tracking-tight leading-[1.06] text-[2rem] sm:text-5xl lg:text-6xl max-w-[14ch] sm:max-w-none">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block text-white"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.18, ease: EASE }}
              >
                {t.hero.line1}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block gold-text"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.34, ease: EASE }}
              >
                {t.hero.line2}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-[52ch] text-base lg:text-lg text-slate-300 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Magnetic>
              <Link to="/contact" data-testid="hero-contact-us-button" className="btn-gold text-xs font-mono2 uppercase tracking-[0.18em]">
                {t.hero.ctaContact} <ArrowRight size={15} />
              </Link>
            </Magnetic>
            <Magnetic>
              <Link to="/driver" data-testid="hero-become-driver-button" className="btn-outline text-xs font-mono2 uppercase tracking-[0.18em]">
                {t.hero.ctaDriver}
              </Link>
            </Magnetic>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.9 }}
        className="relative z-10 border-t border-white/10 bg-black/35 backdrop-blur-md"
      >
        <div className="mx-auto max-w-7xl px-5 lg:px-10 grid grid-cols-2 md:grid-cols-4">
          {t.hero.stats.map((s, i) => (
            <div key={s.l} className={`py-5 pr-5 ${i !== 0 ? "border-l border-white/10 pl-5" : ""}`}>
              <p className="font-mono2 text-xl lg:text-2xl text-gold-soft">{s.v}</p>
              <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-400">{s.l}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function FleetSection() {
  const { t } = useLanguage();
  const models = MODELS(t);
  return (
    <section id="fleet" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <p className="eyebrow">{t.home.fleetEyebrow}</p>
          <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight max-w-[24ch]">
              {t.home.fleetTitle}
            </h2>
            <Link
              to="/fleet"
              data-testid="home-fleet-cta"
              className="inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-gold-soft hover:text-gold-pale transition-colors"
            >
              {t.home.fleetCta} <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {models.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.12}>
              <FleetCard model={m} image={i === 0 ? IMAGES.m3 : IMAGES.my} ctaLabel={t.home.fleetCta} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const { t } = useLanguage();
  return (
    <section className="py-24 lg:py-32 border-t border-white/10 bg-[#090a0e]">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <p className="eyebrow">{t.home.servicesEyebrow}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
            {t.home.servicesTitle}
          </h2>
          <p className="mt-4 max-w-[60ch] text-slate-400">{t.home.servicesSub}</p>
        </Reveal>
        <div className="mt-12">
          {t.servicesPage.items.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <Link
                to="/services"
                data-testid={`service-item-${s.id}`}
                className="group flex items-center justify-between gap-6 border-t border-white/10 py-7 transition-all duration-500 hover:pl-4 last:border-b"
              >
                <div className="flex items-baseline gap-6 lg:gap-10">
                  <span className="font-mono2 text-sm text-gold/70">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-xl lg:text-2xl font-semibold text-white group-hover:text-gold-pale transition-colors duration-300">
                      {s.title}
                    </h3>
                    <p className="mt-2 max-w-[64ch] text-sm text-slate-400">{s.desc}</p>
                  </div>
                </div>
                <ArrowRight size={20} className="shrink-0 text-slate-500 transition-all duration-500 group-hover:text-gold group-hover:translate-x-1" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Estimator() {
  const { t } = useLanguage();
  const [route, setRoute] = useState("bru");
  const [vehicle, setVehicle] = useState("3");
  const r = FARES.routes.find((x) => x.id === route);
  const price = vehicle === "3" ? r.price3 : r.priceY;

  return (
    <section className="py-24 lg:py-32 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 grid gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <p className="eyebrow">{t.home.estEyebrow}</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
            {t.home.estTitle}
          </h2>
          <p className="mt-4 max-w-[50ch] text-slate-400">{t.home.estSub}</p>
          <div
            data-testid="estimator-price"
            className="mt-10 font-mono2 text-5xl lg:text-6xl text-gold-soft"
          >
            €{price}
            {r.hourly && <span className="text-xl text-slate-400">{t.home.hourlyUnit}</span>}
          </div>
          <p className="mt-3 text-xs text-slate-500 font-mono2">{t.home.estNote}</p>
          <Link to="/contact" data-testid="estimator-cta" className="btn-gold mt-8 text-xs font-mono2 uppercase tracking-[0.18em]">
            {t.home.estCta} <ArrowRight size={14} />
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="ride-estimator" className="rounded-2xl border border-white/10 bg-[#0F1015] p-6 lg:p-8">
            <div className="flex flex-col gap-2">
              {FARES.routes.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  data-testid={`estimator-route-${x.id}`}
                  onClick={() => setRoute(x.id)}
                  className={`rounded-xl border px-5 py-4 min-h-[48px] flex items-center text-left text-sm transition-all duration-300 ${
                    route === x.id
                      ? "border-gold/50 bg-gold/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25"
                  }`}
                >
                  {t.home.routes[x.id]}
                </button>
              ))}
            </div>
            <div className="mt-6 flex rounded-full border border-white/10 bg-white/[0.03] p-1">
              {["3", "Y"].map((v) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`estimator-vehicle-model-${v.toLowerCase()}`}
                  onClick={() => setVehicle(v)}
                  className={`flex-1 rounded-full py-2.5 min-h-[44px] font-mono2 text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                    vehicle === v ? "bg-gold text-black font-semibold" : "text-slate-300 hover:text-white"
                  }`}
                >
                  {v === "3" ? t.home.vehicle3 : t.home.vehicleY}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DriverBanner() {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden border-t border-white/10">
      <div className="absolute inset-0">
        <img src={IMAGES.standing} alt="" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-[#070709]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070709] via-transparent to-[#070709]" />
      </div>
      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 py-28 lg:py-36 text-center">
        <Reveal>
          <p className="eyebrow">{t.home.bannerEyebrow}</p>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">
            {t.home.bannerTitle}
          </h2>
          <p className="mx-auto mt-5 max-w-[52ch] text-slate-300">{t.home.bannerSub}</p>
          <Link to="/driver" data-testid="home-driver-banner-cta" className="btn-gold mt-9 text-xs font-mono2 uppercase tracking-[0.18em]">
            {t.home.bannerCta} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ScrollHint() {
  const { t } = useLanguage();
  return (
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 pb-8 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.3em] text-slate-400">
      <ArrowDown size={13} className="text-gold animate-bounce" />
      {t.hero.badge.split("·")[1]?.trim() || "Scroll"}
    </div>
  );
}

export default function Home() {
  useSeo(
    "Tesla Chauffeur Service Antwerp | MIRAL GROUP",
    "Premium Tesla chauffeur service in Antwerp: airport transfers, EU & diplomatic transport, corporate travel and VIP events. 100% electric fleet, 24/24."
  );
  return (
    <main>
      <Hero />
      <Marquee />
      <FleetSection />
      <ServicesSection />
      <Estimator />
      <DriverBanner />
    </main>
  );
}
